import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField, Grid } from '@mui/material'

import BaseDialog from '../../BaseDialog'
import AutoCompleteInput from '../../AutoCompleteInput'

import { useDataApi } from '../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../context/NotificationsContext'

export default function ModifyControlLocationDialog({
  open,
  onClose,
  onModified,
  controlLocationToEdit,
}) {
  const { t } = useTranslation()

  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState(
    controlLocationToEdit && controlLocationToEdit.name
  )
  const [constructors, setConstructors] = useState([])

  const resource =
    'control-location' +
    (controlLocationToEdit && '/' + controlLocationToEdit.id) +
    '/edit'

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError('')
    setName(controlLocationToEdit && controlLocationToEdit.name)
    setConstructors(controlLocationToEdit?.constructors ?? [])
  }

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = () => {
    if (name.length === 0) {
      setError('name_required')
      return
    }

    const body = {
      data: {
        name,
        constructorIds: constructors.map((c) => c.id),
      },
    }

    setLoading(true)
    dataProvider
      .put(resource, body)
      .then(() => {
        dataProvider.cache.reset()
        showNotification('success', t('modify-control-location-dialog.success'))
        onModified()
        resetState()
      })
      .catch((err) => {
        console.log(err)
        if (err.response.data.error === 'CONTROL_LOCATION_ALREADY_EXISTS') {
          showNotification(
            'error',
            t('modify-control-location-dialog.already-exists-error')
          )
        } else if (err.response.data.error === 'CONTROL_LOCATION_NOT_FOUND') {
          showNotification(
            'error',
            t('modify-control-location-dialog.not-found-error')
          )
        } else {
          showNotification('error', t('errors.api-error'))
        }

        setLoading(false)
      })
  }

  useEffect(() => {
    if (controlLocationToEdit) {
      setName(controlLocationToEdit.name)
      setConstructors(controlLocationToEdit.constructors ?? [])
    }
  }, [controlLocationToEdit])

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={t('modify-control-location-dialog.title')}
      loading={loading}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <TextField
            variant="standard"
            required
            error={error === 'name_required'}
            helperText={t('common.required')}
            id="name"
            label={t('modify-control-location-dialog.control-location-name')}
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Grid>
        <Grid item>
          <AutoCompleteInput
            onChange={setConstructors}
            value={constructors}
            label={t('modify-control-location-dialog.constructors')}
            resource="constructors/autocomplete"
            multiple
            canDelete
          />
        </Grid>
      </Grid>
    </BaseDialog>
  )
}
